/* eslint-disable max-len */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchConfiguration } from '../misc/apiRequests';
import PropertyListing from '../functional/properties/propertyListing';
import { islands } from '../misc/jsonData/islandData';
import propTypes from 'prop-types';
import Swal from 'sweetalert2';

import Ad1 from '../../assets/images/ads/ad1.jpeg';
import Ad2 from '../../assets/images/ads/ad2.jpeg';

const homeIslands = [{ label: 'All Islands', name: 'all' }].concat(islands)

const Home = ({user, handlePropertyRequests, handlePropertyRemove, handlePropertySelect, setRedirect, handleLoader, handleModal}) => {
  const { t } = useTranslation();

  const [island, setIsland] = useState('all');
  const [islandQuery, setIslandQuery] = useState('');
  const [bgImage, setBgImage] = useState('');
  const [selectedAdIndex, setSelectedAdIndex] = useState(0);
  const mainPhrase = t('header');
  const slogan = t('header2');

  const btnLinks = useCallback(() => (
    user?.logged_in ? (
      <a href='/account?page=payment' className='mt-05 text-bold btn' style={{border: '1px solid #4cbadc', color: '#fff', backgroundColor: '#4cbadc'}}>
        {t('adSubscribeToday')}
      </a>
    ) : (
      <a href='/sign_up' className='mt-05 text-bold btn' style={{border: '1px solid #dc994c', color: '#fff', backgroundColor: '#dc994c'}}>
        {t('adSignUpToday')}
      </a>
    )
  ), [user?.logged_in, t]);

  // Renders the hero heading/slogan/search form shared by the mobile, medium, and large layouts below
  const heroContent = (h1Class, sloganClass, selectClass, buttonClass) => (
    <>
      <h1 className={h1Class}>{mainPhrase}</h1>
      <div className={sloganClass}>{slogan}</div>
      <form className='nav-search-form' onSubmit={handleSubmit}>
        <input
          type="text"
          list="islandOptions"
          className={selectClass}
          required
          autoComplete="off"
          name="island"
          id="by_island"
          value={islandQuery}
          onChange={e => handleIslandInput(e.target.value)}
        />
        <button type="submit" className={buttonClass}>{t('search')}</button>
      </form>
    </>
  );

  const ads = useMemo(() => [
    (<div className='ad'>
      <h3 className='text-center mb-4'>{t('ad1Title')}</h3>
      {/* <span className='text-grey text-bold'>Ad</span> */}
      <img src={Ad1} alt='advertisement depiction'/>
      <p style={{fontSize: '1.1em'}}>
        <span className='a'>
          {t('ad1IntroText')}
        </span>
        <span className='mt-05'>
          {t('ad1Body')}
        </span>
        {/* <span className='mt-05 text-bold' style={{textIndent: '6px'}}>
          {t('ad1Closing')}
        </span> */}
        <br/>
        {btnLinks()}
      </p>
    </div>),
    (<div className='ad'>
      <h3 className='text-center mb-4'>{t('ad2Title')}</h3>
      {/* <span className='text-grey text-bold'>Ad</span> */}
      <img src={Ad2} alt='advertisement depiction'/>
      <p style={{fontSize: '1.1em'}}>
        <span className='a text-bold mb-05'>
          {t('ad2Intro')}
        </span>
        <br></br>
        <span className='b'>
          {t('ad2Body')}
        </span>
        <span className='mt-05 text-bold' style={{textIndent: '6px'}}>
          {t('ad2Closing')}
        </span>
        <br/>
        {btnLinks()}
      </p>
    </div>),
  ], [t, btnLinks]);

  const determineAd = () => {
    const max = 2; // Number of ads possible
    const randomNum = Math.floor(Math.random() * max);

    setSelectedAdIndex(randomNum);
  };

  const handleSubmit = e => {
    e.preventDefault();
    // setRedirect('/search')
    return null;
  };

  const islandDisplayLabel = data => `${data.label || data.name}, Bahamas`;

  const populateIslands = () => homeIslands.map((data, index) => (
    <option key={index} value={islandDisplayLabel(data)} />
  ));

  // Update the combo box's typed text as the user types, and only commit it as the active
  // island filter once it exactly matches one of the selectable islands (avoids sending
  // free-typed/partial text to the property filter)
  const handleIslandInput = value => {
    setIslandQuery(value);
    const match = homeIslands.find(data => islandDisplayLabel(data).toLowerCase() === value.trim().toLowerCase());
    if (match) setIsland(match.name);
  };

  // Keep the combo box's displayed text in sync whenever the active island changes from elsewhere
  // (e.g. the default island loaded from fetchConfiguration below)
  useEffect(() => {
    const match = homeIslands.find(data => data.name === island);
    if (match) setIslandQuery(islandDisplayLabel(match));
  }, [island]);

  // Grab API Configuration
  useEffect(() => {
    determineAd(); // Set Random Member Advertisement
    handleLoader(true);
    fetchConfiguration()
      .then(response => {
        if (response.success) {
          setIsland(response.record.default_island);
          setBgImage(response.record.home_page_image);
        }
        if (!response.success) {
          // handleModal(response.errors);
          Swal.fire({
            position: 'top-end',
            toast: true,
            timerProgressBar: true,
            icon: 'error',
            title: Array.isArray(response.errors) ? response.errors.join("\n") : response.errors,
            showConfirmButton: false,
            timer: 2000
          })
        }
        handleLoader(false);
      });
  }, [handleLoader, handleModal]);

  return (
    <div>
      <datalist id="islandOptions">
        {populateIslands()}
      </datalist>
      <section id='Main-1'>
        <div className='container-img nav-shadow-sm'>
          <div className='parent-box'>

            {/* Mobile Screens */}
            <div className='overlay-bg-dark hide-xl hide-lg hide-md' />
            <div className='overlay-bg flex-column hide-xl hide-lg hide-md'>
              <div className='container-md'>
                <div className='home-h1 hide-xl hide-lg hide-md'>
                  {heroContent('home-header text-white', 'text-white mb-1 slogan fs-5', 'nav-search-bar fs-5', 'nav-button-search')}
                </div>
              </div>
            </div>

            {/* Medium Screens  */}
            <div className='overlay-bg-dark hide-xl hide-lg hide-sm' />
            <div className='overlay-bg flex-column justify-flex-center hide-xl hide-lg hide-sm'>
              <div className='container'>
                <div className='home-h1 hide-xl hide-lg hide-sm'>
                  {heroContent('home-header text-white mx-auto', 'text-white mb-5 slogan fs-3', 'nav-search-bar fs-4', 'nav-button-search fs-4')}
                </div>
              </div>
            </div>

            {/* Large screens */}
            <div className='home-h1 hide-sm hide-md'>
              {heroContent('home-header text-white', 'text-white mb-5 slogan fs-3', 'nav-search-bar fs-4', 'nav-button-search fs-4')}
            </div>
            <div className='half-horizontal-img' style={{backgroundImage: `url(${bgImage})`}}>
              <div className='overlay-bg hide-sm hide-md' style={{backgroundColor: "#0c13143d"}}></div>
            </div>
          </div>
        </div>
        <div>
          {/* All Properties */}
          <div className='section-b'>
            <div className='container'>
              <div className='px-2 mb-4'>
                <h2 className='text-center sub-header'>{t('header3')} {`${island}, Bah`}</h2>
              </div>
              <PropertyListing
                user={user}
                byIsland={island}
                handlePropertyRequests={handlePropertyRequests}
                handlePropertyRemove={handlePropertyRemove}
                handlePropertySelect={handlePropertySelect}
                setRedirect={setRedirect}
                handleLoader={handleLoader}
                handleModal={handleModal}
                propertyType = 'all' />
            </div>
            <div className='containerx mt-2'>
              {ads[selectedAdIndex]}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

Home.propTypes = {
  user: propTypes.instanceOf(Object).isRequired,
  handlePropertyRequests: propTypes.func.isRequired,
  handlePropertyRemove: propTypes.func.isRequired,
  handlePropertySelect: propTypes.func.isRequired,
  setRedirect: propTypes.func.isRequired,
  handleLoader: propTypes.func.isRequired,
  handleModal: propTypes.func.isRequired,
};

export default Home;
